import { zodResolver } from "@hookform/resolvers/zod";
import { Resolver, SubmitHandler, useForm } from "react-hook-form";
import { TShiftSchema, ShiftSchema, ShiftDefaultValues } from "../Models";
import { Button } from "@/components/ui/button";
import { Dispatch, JSX, SetStateAction, useState } from "react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useTranslation } from "react-i18next";
import { Shift, TenderDurationFragment } from "@/graphql/__generated__/graphql";
import { onInvalid } from "@/utils/form";
import ShiftFormInputs from "./Shared/ShiftFormInputs";
import { SubmitAction } from "@/constants/enums";
import { CircleLoader } from "@/components/shared/loaders";

interface Props {
  showShiftsModal: boolean;
  serviceID: number | undefined;
  onAddShift: (_shift: TShiftSchema, _action: SubmitAction, _setShowShiftsModal: Dispatch<SetStateAction<boolean>>) => Promise<void>;
  setShowShiftsModal: Dispatch<SetStateAction<boolean>>;
  tenderDuration: TenderDurationFragment;
  selectedShift?: Shift;
  isProcessing?: boolean;
};

/**
 * AddShiftComponent form component
 * Wrapped in a Dialog component
 *
 * @param {Props} props
 *
 * @returns {JSX.Element}
 */
const AddShiftComponent = (props: Props): JSX.Element => {
  const { showShiftsModal, setShowShiftsModal, onAddShift, tenderDuration, isProcessing } = props;

  const [formKey, setFormKey] = useState<number>(0);

  // Initialize the form
  const form = useForm<TShiftSchema>({
    resolver: zodResolver(ShiftSchema) as Resolver<TShiftSchema>,
    defaultValues: ShiftDefaultValues,
    mode: "onChange",
  });

  // Hooks
  const { t } = useTranslation(["modals", "common", "translation"]);

  /**
   * Handler function for adding a shift to a service
   * Handles logic for both existing and new services
   */
  const onSubmit: SubmitHandler<TShiftSchema> = async (
    data: TShiftSchema,
    event?: React.BaseSyntheticEvent
  ) => {
    if (!event) {
      console.error("No event provided to onSubmit handler.");
      return;
    }

    try {
      // Safely access the submitter
      const nativeEvent = event.nativeEvent as SubmitEvent;
      const submitter = nativeEvent.submitter;

      if (!(submitter instanceof HTMLButtonElement)) {
        console.warn("Submitter is not an HTMLButtonElement or is undefined.");
        // Handle default action or return early
        return;
      }

      const action = submitter.name as SubmitAction;

      if (!Object.values(SubmitAction).includes(action)) {
        console.warn(`Unexpected submit action: ${action}`);
        // Handle unexpected action or return early
        return;
      }

      // Clear form errors
      form.clearErrors();

      // Call parent function to add shift
      await onAddShift(data, action, setShowShiftsModal);

      // Reset the form after successful submission
      resetForm();
    } catch (error) {
      console.error("Error in onSubmit handler:", error);
    }
  };

  // Resets Shift form only
  const resetForm = () => {
    form.reset(ShiftDefaultValues);
    setFormKey(formKey + 1);
  };

  return (
    <Dialog open={showShiftsModal} onOpenChange={setShowShiftsModal}>
      <DialogContent className="select-none min-w-fit max-w-[90vw] lg:max-w-fit overflow-y-auto max-h-[90vh] h-full md:h-auto">
        <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-6" id="add-shift-form" key={formKey}>
          <DialogHeader className="text-left">
            <DialogTitle>{t("shift.addtitle")}</DialogTitle>
            <DialogDescription>{t("shift.adddescription")}</DialogDescription>
          </DialogHeader>
          {/* form */}
          <ShiftFormInputs
            form={form}
            tenderDuration={tenderDuration}
          />

          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("common:buttons.cancel")}</Button>
            </DialogClose>
            <Button variant="default" type="submit" name={SubmitAction.Add} disabled={isProcessing} className="gap-2">
              <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.add")}
            </Button>
            <Button variant="default" type="submit" name={SubmitAction.AddAndClose} disabled={isProcessing} className="gap-2">
              <CircleLoader visible={isProcessing} height={16} width={16} strokeWidth={2} />
              {t("common:buttons.addandclose")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default AddShiftComponent;
